<template>
    <b-modal
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        @hidden="handleHidden"
        title="적용될 배출계수 미리보기"
        size="lg"
        centered
        ok-only
        ok-title="닫기"
    >
        <template #modal-header="{ close }">
            <h5 class="modal-title">
                <i class="fas fa-eye"></i>
                적용될 배출계수 미리보기
            </h5>
            <button type="button" class="close" @click="close">
                <span aria-hidden="true">&times;</span>
            </button>
        </template>

        <div class="preview-modal-content">
            <!-- 버전 정보 -->
            <div v-if="version" class="version-info-section">
                <div class="info-row">
                    <span class="info-label">버전:</span>
                    <span class="info-value">
                        <strong>{{ version.versionCode }}</strong> ({{ version.versionName }})
                    </span>
                </div>
                <div class="info-row">
                    <span class="info-label">활동 데이터:</span>
                    <span class="info-value">{{ activityDataInfo }}</span>
                </div>
            </div>

            <div class="section-divider">━━━ 사용될 배출계수 ({{ totalFactorCount }}종류) ━━━</div>

            <!-- 기본값 사용 배출계수 -->
            <div v-if="baseValueFactors.length > 0" class="factor-group">
                <div class="group-title">
                    <i class="fas fa-thumbtack"></i>
                    📌 기본값 사용 ({{ baseValueFactors.length }}개)
                </div>
                <div class="factor-list">
                    <div v-for="factor in baseValueFactors" :key="factor.factorCode" class="factor-item base">
                        ├─ {{ factor.factorCode }}: {{ factor.value }} {{ factor.unit }}
                    </div>
                </div>
            </div>

            <!-- 버전별 값 사용 배출계수 -->
            <div v-if="versionValueFactors.length > 0" class="factor-group">
                <div class="group-title star">
                    <i class="fas fa-star"></i>
                    ⭐ 버전별 값 사용 ({{ versionValueFactors.length }}개)
                </div>
                <div class="factor-list">
                    <div v-for="factor in versionValueFactors" :key="factor.factorCode" class="factor-item version">
                        <div class="factor-main">
                            └─ {{ factor.factorCode }}: {{ factor.versionValue }} {{ factor.unit }}
                            <span class="change-badge" :class="factor.changeDirection">
                                (기본: {{ factor.baseValue }}, {{ factor.changeText }})
                            </span>
                        </div>
                        <div class="factor-reason">└─ 사유: {{ factor.reason }}</div>
                    </div>
                </div>
            </div>

            <div class="section-divider">━━━ 예상 배출량 ━━━</div>

            <!-- 예상 배출량 -->
            <div class="estimated-emission">
                <div class="emission-total">
                    <div class="total-label">총 예상 배출량:</div>
                    <div class="total-value">{{ totalEmission }} tonCO2eq</div>
                </div>

                <div class="emission-breakdown">
                    <div v-for="item in emissionBreakdown" :key="item.fuelType" class="breakdown-item">
                        <span class="fuel-name">{{ item.fuelType }}:</span>
                        <span class="emission-value">{{ item.emission }} ton</span>
                        <span class="calculation">({{ item.calculation }})</span>
                    </div>
                </div>
            </div>

            <!-- 도움말 -->
            <div v-if="version" class="help-box">
                <i class="fas fa-info-circle"></i>
                버전별 값이 적용된 항목으로 인해
                <template v-if="comparisonInfo">
                    {{ comparisonInfo.baseVersion }} 대비 약
                    <strong :class="comparisonInfo.direction">
                        {{ comparisonInfo.difference }} ton ({{ comparisonInfo.percent }}%)
                    </strong>
                    {{ comparisonInfo.trend }}
                </template>
            </div>

            <!-- 버전 정보 없을 때 -->
            <div v-if="!version" class="no-version-message">
                <i class="fas fa-exclamation-circle"></i>
                <p>버전 정보를 불러올 수 없습니다.</p>
            </div>
        </div>

        <template #modal-footer="{ ok }">
            <button type="button" class="btn btn-secondary" @click="ok">이전</button>
            <button type="button" class="btn btn-success" @click="handleExecuteCalc">
                <i class="fas fa-calculator"></i>
                계산 실행
            </button>
        </template>
    </b-modal>
</template>

<script>
export default {
    name: "CalcPreviewModal",

    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        version: {
            type: Object,
            default: null,
        },
        calcParams: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            // 미리보기 데이터 (실제로는 API에서 가져와야 함)
            baseValueFactors: [
                { factorCode: "KR-DIESEL-L", value: 2.7, unit: "kgCO2/L" },
                { factorCode: "KR-GAS-NM3", value: 1.95, unit: "kgCO2/Nm3" },
            ],
            versionValueFactors: [
                {
                    factorCode: "KR-LNG-NM3",
                    versionValue: 2.3,
                    baseValue: 2.2,
                    unit: "kgCO2/Nm3",
                    changeText: "+4.5%",
                    changeDirection: "increase",
                    reason: "환경부 고시 2024-001호 반영",
                },
            ],
            emissionBreakdown: [
                { fuelType: "LNG", emission: "1,150", calculation: "2.3 × 500 Nm3" },
                { fuelType: "경유", emission: "540", calculation: "2.7 × 200 L" },
                { fuelType: "도시가스", emission: "390", calculation: "1.95 × 200 Nm3" },
                { fuelType: "전력", emission: "270", calculation: "0.459 × 588 kWh" },
            ],
            totalEmission: "2,350",
            comparisonInfo: {
                baseVersion: "v2023-KR",
                difference: "+50",
                percent: "+2.2",
                direction: "increase",
                trend: "증가 예상",
            },
        };
    },

    computed: {
        totalFactorCount() {
            return this.baseValueFactors.length + this.versionValueFactors.length;
        },

        activityDataInfo() {
            if (!this.calcParams || !this.calcParams.from || !this.calcParams.to) {
                return "-";
            }
            return `${this.calcParams.from} ~ ${this.calcParams.to} (예상 23건)`;
        },
    },

    methods: {
        handleExecuteCalc() {
            this.$emit("execute");
            this.$emit("update:visible", false);
        },

        handleHidden() {
            this.$emit("update:visible", false);
        },
    },
};
</script>

<style scoped>
.preview-modal-content {
    padding: 10px 0;
}

.version-info-section {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
    border-left: 4px solid #52b788;
}

.info-row {
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
    font-size: 14px;
}

.info-row:last-child {
    margin-bottom: 0;
}

.info-label {
    color: #6c757d;
    font-weight: 500;
    min-width: 100px;
}

.info-value {
    color: #2c3e50;
}

.info-value strong {
    color: #52b788;
    font-size: 15px;
}

.section-divider {
    font-size: 14px;
    font-weight: 600;
    color: #495057;
    margin: 24px 0 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e2e8f0;
}

.factor-group {
    margin-bottom: 24px;
}

.group-title {
    font-size: 14px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.group-title.star {
    color: #ffa500;
}

.group-title i {
    font-size: 13px;
}

.factor-list {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
    font-family: "Courier New", monospace;
    font-size: 13px;
}

.factor-item {
    margin-bottom: 8px;
    color: #495057;
}

.factor-item:last-child {
    margin-bottom: 0;
}

.factor-item.base {
    color: #6c757d;
}

.factor-item.version {
    color: #2c3e50;
}

.factor-main {
    display: flex;
    align-items: center;
    gap: 8px;
}

.change-badge {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 500;
}

.change-badge.increase {
    background: #ffe5e5;
    color: #dc3545;
}

.change-badge.decrease {
    background: #e8f5ee;
    color: #28a745;
}

.factor-reason {
    margin-left: 12px;
    margin-top: 4px;
    font-size: 12px;
    color: #6c757d;
}

.estimated-emission {
    background: #e8f5ee;
    padding: 20px;
    border-radius: 8px;
    border: 2px solid #52b788;
}

.emission-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #d4edda;
}

.total-label {
    font-size: 16px;
    font-weight: 600;
    color: #2d8659;
}

.total-value {
    font-size: 24px;
    font-weight: 700;
    color: #52b788;
}

.emission-breakdown {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.breakdown-item {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
}

.fuel-name {
    min-width: 80px;
    color: #495057;
    font-weight: 500;
}

.emission-value {
    min-width: 100px;
    font-weight: 600;
    color: #2d8659;
}

.calculation {
    font-size: 13px;
    color: #6c757d;
    font-family: "Courier New", monospace;
}

.help-box {
    margin-top: 20px;
    padding: 15px;
    background: #fff3cd;
    border-left: 3px solid #ffc107;
    border-radius: 4px;
    font-size: 13px;
    color: #856404;
    line-height: 1.6;
}

.help-box i {
    margin-right: 6px;
}

.help-box strong {
    font-weight: 600;
}

.help-box strong.increase {
    color: #dc3545;
}

.help-box strong.decrease {
    color: #28a745;
}

.no-version-message {
    text-align: center;
    padding: 40px 20px;
    color: #6c757d;
}

.no-version-message i {
    font-size: 48px;
    color: #e2e8f0;
    margin-bottom: 16px;
}

.no-version-message p {
    font-size: 14px;
    margin: 0;
}
</style>
